/**
 * A domain reference attached to an IP exclusion. When an exclusion applies to
 * every domain in the account, the API returns an empty `domains` array (and/or
 * an `account` flag), which the UI renders as "All".
 */
export interface IpExclusionDomain {
  uuid: string;
  name: string;
}

/**
 * A single IP exclusion row as returned by the API (already camelCased at the
 * service boundary).
 */
export interface IpExclusion {
  uuid: string;
  ipAddress: string;
  /** Empty array => applies to all domains in the account. */
  domains: IpExclusionDomain[];
  /** True when the exclusion applies to the whole account (all domains). */
  account?: boolean;
  blockedUntil: string | null;
  author: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface IpExclusionsListMeta {
  currentPage: number;
  perPage: number;
  total: number;
  totalPages: number;
}

export interface IpExclusionsListResponse {
  data: IpExclusion[];
  meta: IpExclusionsListMeta;
}

export interface IpExclusionStatistics {
  total: number;
}

/**
 * Payload sent to the create/edit endpoints. The service converts these camelCase
 * keys to snake_case (`ip_address`, `domain_uuids`, `blocked_until`) before the
 * request. NOTE: `description` is intentionally not part of this payload — the
 * backend does not accept it (the field is form-only per design).
 */
export interface IpExclusionSubmitPayload {
  uuid?: string;
  ipAddress: string;
  domainUuids: string[];
  blockedUntil: string | null;
}

export interface BulkDeleteIpExclusionsPayload {
  exclusionIpUuids: string[];
  allSelected: boolean;
}

/**
 * Values collected by the create/edit drawer form. `description` is captured for
 * UX parity with the design but is never sent to the API.
 */
export interface IpExclusionFormValue {
  uuid?: string;
  ipAddress: string;
  domains: string[];
  description: string;
  blockedUntil: string | null;
}

export const DEFAULT_IP_EXCLUSIONS_CURRENT_PAGE = 1;
export const DEFAULT_IP_EXCLUSIONS_PER_PAGE = 25;
export const IP_EXCLUSIONS_PAGE_SIZE_OPTIONS = [
  DEFAULT_IP_EXCLUSIONS_PER_PAGE,
  50,
  100,
] as const;

/**
 * IPv4 / IPv6 validation regex ported verbatim from the React app
 * (`constants/ipExclusions.js`).
 */
export const IP_ADDRESS_REGEX =
  /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$|^([0-9a-fA-F]{1,4}:){7,7}[0-9a-fA-F]{1,4}$|^([0-9a-fA-F]{1,4}:){1,7}:$|^[0-9a-fA-F]{1,4}:([0-9a-fA-F]{1,4}:){1,6}[0-9a-fA-F]{1,4}$|^[0-9a-fA-F]{1,4}:([0-9a-fA-F]{1,4}:){1,6}(:[0-9a-fA-F]{1,4}){1,2}$/;

export const IP_EXCLUSION_FIELD_HELP = {
  ipAddress:
    'Add an IP address to IP Exclusions. Clicks and scans from this IP address will automatically be filtered from the analytics.',
  domains: 'Select the domains that apply to this IP Exclusion.',
  blockedUntil:
    'Specify the exclusion date for an IP address. After this date, clicks and scans from this IP will be counted.',
} as const;

export function createEmptyIpExclusionFormValue(): IpExclusionFormValue {
  return {
    ipAddress: '',
    domains: [],
    description: '',
    blockedUntil: null,
  };
}
